import { Observable, EMPTY, concat, of, from } from 'rxjs';
import { catchError, endWith, mergeMap, take, takeUntil, filter, finalize } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { ModalService } from 'ui-components/services/modal-service';

import { BulkUpdateModal } from '../../../components/modals/bulk-update-modal';
import Global from '../../../store/global-selectors';
import { BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID } from '../../../store/orders/drawer/constants';
import { BulkUpdateService } from '../../../services/bulk-update-service';
import { BulkActionJobValidator } from '../../../services/bulk-action-job-validator';
import { SET_SELECTED_ORDERS } from '../../../store/orders/selected-orders/orders.selected-orders.actions';
import * as Actions from './bulk.update.actions';

import { bulkUpdateReadScaleRequest$, onChangeModel$, onSetModel$ } from './bulk.update.observables';
import { onValidateBulkUpdateJob$, bulkUpdateRequestFromModal$, bulkUpdateRequestFromDrawer$ } from './bulk.update.request.observables';
import { launchValidationErrorModal$, getBulkJobData$ } from './bulk.update.job.observables';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';

const launchBulkUpdateModal = (bulkUpdateJobId, observer) => {
  const modalId = ModalService.launchModal({
    component: BulkUpdateModal,
    onClose: () => {
      observer.next(of(Actions.deleteBulkUpdateJob(bulkUpdateJobId)));
      observer.complete();
    },
    onCancel: () => {
      observer.next(of(Actions.deleteBulkUpdateJob(bulkUpdateJobId)));
    },
    props: {
      bulkUpdateJobId,
    },
  });
  return modalId;
};

export const onJobStarted = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.START_BULK_UPDATE_JOB),
    mergeMap(({ filters, id: bulkUpdateJobId, bulkActions }) => {
      return new Observable((observer) => {
        observer.next(
          getBulkJobData$(bulkUpdateJobId, filters, getState, history).pipe(
            mergeMap((action) => {
              const state = getState();
              const [, configData] = Global.selectCountryFeatureConfig(state, CountryConfigNames.ShippingStrategy);
              const { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate } = configData ?? {};

              return concat(
                of(action),
                from(
                  BulkActionJobValidator.validate(bulkActions, action.bulkUpdateJobData, {
                    ignoreConfirmationBulkUpdate,
                    ignorePackageBulkUpdate,
                  }),
                ).pipe(
                  mergeMap(() => EMPTY),
                  catchError(launchValidationErrorModal$),
                ),
              );
            }),
          ),
        );

        const modalId = launchBulkUpdateModal(bulkUpdateJobId, observer);

        observer.next(onChangeModel$(action$, getState, bulkUpdateJobId));
        observer.next(bulkUpdateReadScaleRequest$(action$, getState));
        observer.next(onValidateBulkUpdateJob$(action$, getState, bulkUpdateJobId, bulkUpdateRequestFromModal$, modalId));
      }).pipe(mergeMap((next$) => next$));
    }),
  );
};

const bulkIsNoLongerActiveForDrawer = (action) => {
  return action.type === SET_SELECTED_ORDERS && action.selectedOrderIds.length < 2;
};

const checkForEmptyBulkActionsInDrawer$ = (action$, getState) => {
  return action$.pipe(
    ofType(Actions.BULK_UPDATE_VALIDATE_JOB_FOR_DRAWER),
    mergeMap(({ bulkUpdateJobId }) => {
      const state = getState();
      const { model, bulkActions } = Global.selectBulkUpdateBulkUpdateJob(state, { bulkUpdateJobId });
      const emptyBulkActions = bulkActions.filter((bulkAction) => BulkUpdateService.modelIsEmptyForBulkAction(model, bulkAction));
      const actions = [Actions.updateBulkUpdateJob(bulkUpdateJobId, { emptyBulkActions })];
      if (emptyBulkActions.length === 0) {
        actions.push(Actions.validateBulkUpdateJob(bulkUpdateJobId));
      }
      return concat(actions);
    }),
    takeUntil(action$.pipe(filter(bulkIsNoLongerActiveForDrawer))),
  );
};

const shutOffSourceWhenFromDrawer$ = (action$, bulkUpdateJobId, observer) => {
  return action$.pipe(
    filter(bulkIsNoLongerActiveForDrawer),
    take(1),
    mergeMap(() => of(Actions.deleteBulkUpdateJob(bulkUpdateJobId))),
    finalize(() => observer.complete()),
  );
};

export const onJobStartedFromDrawer = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.START_BULK_UPDATE_JOB_FROM_DRAWER),
    mergeMap(({ filters, id: bulkUpdateJobId }) => {
      return new Observable((observer) => {
        observer.next(getBulkJobData$(bulkUpdateJobId, filters, getState, history));

        if (bulkUpdateJobId === BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID) {
          observer.next(checkForEmptyBulkActionsInDrawer$(action$, getState));
          observer.next(onChangeModel$(action$, getState, bulkUpdateJobId));
        }

        observer.next(onSetModel$(action$, getState, bulkUpdateJobId));
        observer.next(bulkUpdateReadScaleRequest$(action$, getState));

        observer.next(
          onValidateBulkUpdateJob$(action$, getState, bulkUpdateJobId, bulkUpdateRequestFromDrawer$).pipe(
            endWith(
              Actions.updateBulkUpdateJob(bulkUpdateJobId, {
                isLoading: false,
              }),
            ),
          ),
        );

        observer.next(shutOffSourceWhenFromDrawer$(action$, bulkUpdateJobId, observer));
      }).pipe(mergeMap((next$) => next$));
    }),
  );
};

export const onDoNotNotifyMarketPlaceJobStarted = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.START_BULK_UPDATE_DO_NOT_NOTIFY_MARKETPLACE_JOB),
    mergeMap((action) => {
      const { filters, bulkActions, id: bulkUpdateJobId } = action;
      return getBulkJobData$(bulkUpdateJobId, filters, getState, history).pipe(
        mergeMap((successAction) => {
          const state = getState();
          const [, configData] = Global.selectCountryFeatureConfig(state, CountryConfigNames.ShippingStrategy);
          const { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate } = configData ?? {};

          return concat(
            of(successAction),
            from(
              BulkActionJobValidator.validate(bulkActions, successAction.bulkJobData, {
                ignoreConfirmationBulkUpdate,
                ignorePackageBulkUpdate,
              }),
            ).pipe(
              mergeMap(() => {
                return bulkUpdateRequestFromModal$(
                  getState,
                  action,
                  Global.selectBulkUpdateBulkUpdateJob(getState(), {
                    bulkUpdateJobId,
                  }),
                );
              }),
              catchError(launchValidationErrorModal$),
            ),
          );
        }),
        endWith(Actions.deleteBulkUpdateJob(bulkUpdateJobId)),
      );
    }),
  );
};
